import * as React from "react";
import { CalendarDays } from "lucide-react";

import { cn } from "@/lib/utils";

// Dates are typed and shown as dd/mm/yyyy everywhere, whatever the browser's language. The value going in and out stays
// yyyy-mm-dd (what <input type="date"> gives), so every form and the API keep working unchanged.

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isoToDmy(iso) {
  const m = ISO.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

function validIso(y, mo, d) {
  const date = new Date(Date.UTC(+y, +mo - 1, +d));
  return date.getUTCFullYear() === +y && date.getUTCMonth() === +mo - 1 && date.getUTCDate() === +d ? `${y}-${mo}-${d}` : null;
}

/** "dd/mm/yyyy" (or a pasted "yyyy-mm-dd") -> "yyyy-mm-dd", or null when it isn't a real date. */
export function dmyToIso(text) {
  const iso = ISO.exec(text || "");
  if (iso) return validIso(iso[1], iso[2], iso[3]);
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text || "");
  return m ? validIso(m[3], m[2], m[1]) : null;
}

/** Keeps digits only and puts the slashes in: "3010202" -> "30/10/202". */
function mask(raw) {
  if (ISO.test(raw || "")) return isoToDmy(raw);
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join("/");
}

const asEvent = (value, name, id) => {
  const target = { value, name, id };
  return { target, currentTarget: target, preventDefault() {}, stopPropagation() {} };
};

const DateInput = React.forwardRef(
  ({ className, value, defaultValue, onChange, onBlur, min, max, name, id, disabled, readOnly, required, placeholder, ...props }, ref) => {
    const controlled = value !== undefined;
    const [inner, setInner] = React.useState(defaultValue ?? "");
    const iso = controlled ? value ?? "" : inner;
    const [text, setText] = React.useState(() => isoToDmy(iso));
    const textRef = React.useRef(null);
    const pickerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => textRef.current);

    // the value changed from outside (a reset, a default filled in): show it
    React.useEffect(() => {
      setText((current) => (dmyToIso(current) === (iso || null) ? current : isoToDmy(iso)));
    }, [iso]);

    const checkRange = (next) => {
      const el = textRef.current;
      if (!el) return;
      let msg = "";
      if (next && min && next < min) msg = `Choose a date on or after ${isoToDmy(min)}`;
      else if (next && max && next > max) msg = `Choose a date on or before ${isoToDmy(max)}`;
      el.setCustomValidity(msg);
    };

    const commit = (next) => {
      if (!controlled) setInner(next);
      checkRange(next);
      onChange?.(asEvent(next, name, id));
    };

    const handleText = (e) => {
      const shown = mask(e.target.value);
      setText(shown);
      if (!shown) return commit("");
      const next = dmyToIso(shown);
      if (next) commit(next);
      else textRef.current?.setCustomValidity(shown.length === 10 ? "That date doesn't exist" : "Enter the date as dd/mm/yyyy");
    };

    const handleBlur = (e) => {
      if (text && !dmyToIso(text)) textRef.current?.setCustomValidity(text.length === 10 ? "That date doesn't exist" : "Enter the date as dd/mm/yyyy");
      onBlur?.(e);
    };

    const openPicker = () => {
      const picker = pickerRef.current;
      if (!picker || disabled || readOnly) return;
      try {
        picker.showPicker();
      } catch {
        picker.focus();
        picker.click();
      }
    };

    return (
      <div className={cn("relative flex h-9 w-full", className)}>
        <input
          {...props}
          ref={textRef}
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          placeholder={placeholder || "dd/mm/yyyy"}
          value={text}
          onChange={handleText}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          className="flex h-full w-full rounded-md border border-input bg-transparent py-1 pl-3 pr-9 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label="Open calendar"
          onClick={openPicker}
          disabled={disabled || readOnly}
          className="absolute right-1 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
        >
          <CalendarDays className="h-4 w-4" />
        </button>
        {/* the browser's own calendar, opened by the button; it only ever deals in yyyy-mm-dd. It lies invisibly over the whole
            field so the calendar drops down just under the field, left edges lined up (a zero-size box has no position, and the
            browser would open the calendar in the corner of the screen). Clicks pass through it to the text and the button. */}
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          value={dmyToIso(text) || ""}
          min={min}
          max={max || "9999-12-31"}
          onChange={(e) => { setText(isoToDmy(e.target.value)); commit(e.target.value); }}
          className="pointer-events-none absolute inset-0 h-full w-full border-0 p-0 opacity-0"
        />
      </div>
    );
  }
);
DateInput.displayName = "DateInput";

export { DateInput };
